import { useQueryClient } from '@tanstack/react-query';
import {
  getGetTasksQueryKey,
  useBumpTask,
  useRecallTask,
  type KdsTaskDetail,
  type KdsTaskDetailStatus,
} from '@ordr-as/ordr-service-types/epos';

function nextStatusOnBump(status: KdsTaskDetailStatus | undefined): KdsTaskDetailStatus | undefined {
  if (status === 'QUEUED') return 'ONGOING';
  if (status === 'ONGOING' || status === 'RECALLED') return 'DONE';
  return undefined;
}

/**
 * Bump (advance) and recall (undo) with optimistic cache updates. The server
 * response carries the authoritative status, which corrects the optimistic
 * guess in onSuccess; onSettled refetches the full truth.
 */
export function useKdsTaskMutations(locationId: number) {
  const queryClient = useQueryClient();
  const tasksQueryKey = getGetTasksQueryKey(locationId);

  const patchTask = (taskId: number, patch: (task: KdsTaskDetail) => KdsTaskDetail) => {
    queryClient.setQueryData<KdsTaskDetail[]>(tasksQueryKey, (current) =>
      current?.map((task) => (task.id === taskId ? patch(task) : task))
    );
  };

  const applyOptimisticStatus = async (
    taskId: number,
    toStatus: (task: KdsTaskDetail) => KdsTaskDetailStatus | undefined
  ) => {
    await queryClient.cancelQueries({ queryKey: tasksQueryKey });
    const previous = queryClient.getQueryData<KdsTaskDetail[]>(tasksQueryKey);
    patchTask(taskId, (task) => {
      const status = toStatus(task);
      return status ? { ...task, status, updatedUtc: new Date().toISOString() } : task;
    });
    return { previous };
  };

  const rollback = (context: { previous?: KdsTaskDetail[] } | undefined) => {
    if (context?.previous) queryClient.setQueryData(tasksQueryKey, context.previous);
  };

  const reconcile = (taskId: number, status: KdsTaskDetailStatus | undefined) => {
    if (status) patchTask(taskId, (task) => ({ ...task, status }));
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: tasksQueryKey });

  const bumpMutation = useBumpTask({
    mutation: {
      onMutate: ({ taskId }) => applyOptimisticStatus(taskId, (task) => nextStatusOnBump(task.status)),
      onSuccess: (response, { taskId }) => reconcile(taskId, response.status),
      onError: (_error, _vars, context) => rollback(context),
      onSettled: invalidate,
    },
  });

  const recallMutation = useRecallTask({
    mutation: {
      onMutate: ({ taskId }) => applyOptimisticStatus(taskId, () => 'ONGOING'),
      onSuccess: (response, { taskId }) => reconcile(taskId, response.status),
      onError: (_error, _vars, context) => rollback(context),
      onSettled: invalidate,
    },
  });

  return {
    bump: (taskId: number) => bumpMutation.mutate({ locationId, taskId }),
    recall: (taskId: number) => recallMutation.mutate({ locationId, taskId }),
    pendingTaskIds: new Set(
      [
        bumpMutation.isPending ? bumpMutation.variables?.taskId : undefined,
        recallMutation.isPending ? recallMutation.variables?.taskId : undefined,
      ].filter((id): id is number => id !== undefined)
    ),
  };
}
